import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  Divider,
  IconButton
} from '@mui/material';
import {
  Help as HelpIcon,
  SettingsEthernetOutlined as TestTubeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const Hl7MappingDialogs = ({
  mappingDialogOpen,
  closeMappingDialog,
  editingMapping,
  mappingForm,
  handleMappingInputChange,
  handleMappingRuleChange,
  testCurrentMapping,
  saveMapping,
  loading,
  inlineTestResult,
  fieldErrors,
  setFieldErrors,
  setHelpDialogOpen,
  helpDialogOpen,
  hl7TestDialogOpen,
  closeTestDialog,
  hl7TestMapping,
  hl7TestMessage,
  setHl7TestMessage,
  testMapping,
  hl7TestResult
}) => {
  const getFieldErrorMessage = (status, fieldName) => {
    switch (status) {
      case 'not_configured':
        return t('admin.hl7Mapping.testResults.errors.notConfigured');
      case 'segment_not_found':
        return t('admin.hl7Mapping.testResults.errors.segmentNotFound');
      case 'field_not_found':
        return t('admin.hl7Mapping.testResults.errors.fieldNotFound');
      case 'field_empty':
        return t('admin.hl7Mapping.testResults.errors.fieldEmpty');
      case 'error':
        return t('admin.hl7Mapping.testResults.errors.unknownError');
      default:
        return t('admin.hl7Mapping.testResults.errors.unknownError');
    }
  };
  const { t } = useTranslation();

  // Render mapping rule field
  const renderMappingRuleField = (fieldName, rule) => {
    const hasError = fieldErrors.has(fieldName);
    
    return (
      <Grid container spacing={1} key={fieldName} sx={{ mb: 2, alignItems: 'center' }}>
        <Grid item sx={{ width: '200px', flexShrink: 0 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
            {t(`admin.hl7Mapping.fields.${fieldName}`)}
          </Typography>
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            size="small"
            label={t('admin.hl7Mapping.segment')}
            value={rule.segment || ''}
            onChange={(e) => {
              handleMappingRuleChange(fieldName, { ...rule, segment: e.target.value });
              if (hasError) {
                setFieldErrors(prev => {
                  const newErrors = new Set(prev);
                  newErrors.delete(fieldName);
                  return newErrors;
                });
              }
            }}
            fullWidth
            error={hasError}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasError ? 'error.main' : undefined,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            size="small"
            label={t('admin.hl7Mapping.counter')}
            type="number"
            value={rule.counter || ''}
            onChange={(e) => {
              handleMappingRuleChange(fieldName, { ...rule, counter: e.target.value });
              if (hasError) {
                setFieldErrors(prev => {
                  const newErrors = new Set(prev);
                  newErrors.delete(fieldName);
                  return newErrors;
                });
              }
            }}
            placeholder="1, 2, 3..."
            inputProps={{ min: 1 }}
            fullWidth
            error={hasError}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasError ? 'error.main' : undefined,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            size="small"
            label={t('admin.hl7Mapping.field')}
            type="number"
            value={rule.field || ''}
            onChange={(e) => {
              handleMappingRuleChange(fieldName, { ...rule, field: parseInt(e.target.value) || 0 });
              if (hasError) {
                setFieldErrors(prev => {
                  const newErrors = new Set(prev);
                  newErrors.delete(fieldName);
                  return newErrors;
                });
              }
            }}
            fullWidth
            error={hasError}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasError ? 'error.main' : undefined,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            size="small"
            label={t('admin.hl7Mapping.subfield')}
            value={rule.subfield || ''}
            onChange={(e) => {
              handleMappingRuleChange(fieldName, { ...rule, subfield: e.target.value });
              if (hasError) {
                setFieldErrors(prev => {
                  const newErrors = new Set(prev);
                  newErrors.delete(fieldName);
                  return newErrors;
                });
              }
            }}
            placeholder="0, 2,3,4,5"
            fullWidth
            error={hasError}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasError ? 'error.main' : undefined,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            size="small"
            label={t('admin.hl7Mapping.format')}
            value={rule.format || ''}
            onChange={(e) => {
              handleMappingRuleChange(fieldName, { ...rule, format: e.target.value });
              if (hasError) {
                setFieldErrors(prev => {
                  const newErrors = new Set(prev);
                  newErrors.delete(fieldName);
                  return newErrors;
                });
              }
            }}
            placeholder="first_last, date"
            fullWidth
            error={hasError}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasError ? 'error.main' : undefined,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={rule.multiple || false}
                onChange={(e) => {
                  handleMappingRuleChange(fieldName, { ...rule, multiple: e.target.checked });
                  if (hasError) {
                    setFieldErrors(prev => {
                      const newErrors = new Set(prev);
                      newErrors.delete(fieldName);
                      return newErrors;
                    });
                  }
                }}
                size="small"
              />
            }
            label={t('admin.hl7Mapping.multiple')}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {/* HL7 Mapping Dialog */}
      <Dialog open={mappingDialogOpen} onClose={closeMappingDialog} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ position: 'relative' }}>
          {editingMapping ? t('admin.hl7Mapping.editMapping') : t('admin.hl7Mapping.addMapping')}
          <IconButton
            onClick={() => setHelpDialogOpen(true)}
            sx={{ 
              position: 'absolute', 
              right: 8, 
              top: 8,
              color: 'var(--rd-primary)'
            }}
          >
            <HelpIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* First row: Name, Source System, Description, Active Switch */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ width: '400px', flexShrink: 0 }}>
                <TextField
                  fullWidth
                  label={t('admin.hl7Mapping.name')}
                  value={mappingForm.name}
                  onChange={(e) => handleMappingInputChange('name', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ width: '100px', flexShrink: 0 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.hl7Mapping.sourceSystem')}</InputLabel>
                  <Select
                    value={mappingForm.source_system}
                    onChange={(e) => handleMappingInputChange('source_system', e.target.value)}
                    label={t('admin.hl7Mapping.sourceSystem')}
                  >
                    <MenuItem value="ris">RIS</MenuItem>
                    <MenuItem value="kis">KIS</MenuItem>
                    <MenuItem value="pacs">PACS</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 auto' }}>
                <TextField
                  fullWidth
                  label={t('admin.hl7Mapping.description')}
                  value={mappingForm.description}
                  onChange={(e) => handleMappingInputChange('description', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mappingForm.is_active}
                      onChange={(e) => handleMappingInputChange('is_active', e.target.checked)}
                    />
                  }
                  label={t('admin.hl7Mapping.active')}
                />
              </Box>
            </Box>
            
            {/* Second row: Sample Message - full width */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label={t('admin.hl7Mapping.sampleMessage')}
                value={mappingForm.sample_message}
                onChange={(e) => handleMappingInputChange('sample_message', e.target.value)}
                placeholder="MSH|^~\&|KIS|Klinik|RIS|Radiologie|20250619100000||ORM^O01|123456|P|2.3.1..."
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            {/* Third row: Mapping Rules */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('admin.hl7Mapping.mappingRules')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {Object.entries(mappingForm.mapping_rules).map(([fieldName, rule]) => 
                renderMappingRuleField(fieldName, rule)
              )}
            </Box>

            {/* Inline Test Results */}
            {inlineTestResult && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.hl7Mapping.testResults.title')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Overall Status */}
                <Alert 
                  severity={inlineTestResult.success ? 'success' : 'warning'} 
                  sx={{ mb: 2 }}
                >
                  {inlineTestResult.success 
                    ? t('admin.hl7Mapping.testResults.allSuccess', { count: Object.keys(inlineTestResult.detailed_results || {}).length })
                    : t('admin.hl7Mapping.testResults.someErrors', { 
                        successCount: Object.keys(inlineTestResult.parsed_fields || {}).length,
                        errorCount: (inlineTestResult.errors || []).length 
                      })
                  }
                </Alert>

                {/* Detailed Results */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(inlineTestResult.detailed_results || {})
                    .sort(([fieldNameA], [fieldNameB]) => {
                      const aHasError = fieldErrors.has(fieldNameA);
                      const bHasError = fieldErrors.has(fieldNameB);
                      if (aHasError && !bHasError) return -1;
                      if (!aHasError && bHasError) return 1;
                      return 0;
                    })
                    .map(([fieldName, result]) => {
                      const hasError = fieldErrors.has(fieldName);
                      
                      return (
                        <Card 
                          key={fieldName}
                          sx={{ 
                            p: 2,
                            border: hasError ? '2px solid var(--rd-error)' : '1px solid var(--rd-divider)',
                            backgroundColor: hasError ? 'color-mix(in srgb, var(--rd-error) 12%, transparent)' : 'var(--rd-bg-subtle)'
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {t(`admin.hl7Mapping.fields.${fieldName}`, fieldName)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="textSecondary">
                                {t('admin.hl7Mapping.testResults.configuration')}: {result.segment}|{result.field_index}.{result.subfield_index} {result.counter ? `[${result.counter}]` : ''} {result.format_type ? `(${result.format_type})` : ''}
                              </Typography>
                              <Alert 
                                severity={hasError ? 'error' : 'success'} 
                                sx={{ py: 0, px: 1 }}
                              >
                                {hasError ? t('admin.hl7Mapping.testResults.error') : t('admin.hl7Mapping.testResults.success')}
                              </Alert>
                            </Box>
                            <Typography variant="body2">
                              <strong>{t('admin.hl7Mapping.testResults.rawValue')}:</strong> {result.raw_values && result.raw_values.length > 0 ? result.raw_values.join(', ') : 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{hasError ? t('admin.hl7Mapping.testResults.error') : t('admin.hl7Mapping.testResults.processedValue')}:</strong> {hasError ? getFieldErrorMessage(result.status, fieldName) : result.final_value || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      );
                    })
                  }
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            onClick={testCurrentMapping}
            variant="contained"
            startIcon={<TestTubeIcon />}
            disabled={loading || !mappingForm.sample_message.trim()}
            className="button"
            sx={{ mr: 'auto' }}
          >
            {t('admin.hl7Mapping.testMapping')}
          </Button>
          <Box>
            <Button onClick={closeMappingDialog} className="button-cancel" sx={{ mr: 1 }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={saveMapping} 
              variant="contained" 
              disabled={loading}
              className="button"
            >
              {loading ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={hl7TestDialogOpen} onClose={closeTestDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('admin.hl7Mapping.testMapping')} - {hl7TestMapping?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            label={t('admin.hl7Mapping.testMessage')}
            value={hl7TestMessage}
            onChange={(e) => setHl7TestMessage(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {hl7TestResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('admin.hl7Mapping.testResult')}
              </Typography>
              {hl7TestResult.success ? (
                <Alert severity="success">
                  <Typography variant="body2" gutterBottom>
                    {t('admin.hl7Mapping.testSuccess')}
                  </Typography>
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(hl7TestResult.parsed_fields, null, 2)}
                  </pre>
                </Alert>
              ) : (
                <Alert severity="error">
                  {hl7TestResult.error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTestDialog} className="button-cancel">
            {t('common.close')}
          </Button>
          <Button 
            onClick={testMapping} 
            variant="contained" 
            disabled={loading || !hl7TestMessage.trim()}
            className="button"
          >
            {loading ? t('admin.hl7Mapping.testing') : t('admin.hl7Mapping.test')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Hl7MappingDialogs; 